import React from 'react';
import axios from "../../interceptors/interceptors";
import ReactMarkdown from 'react-markdown';
import InfoButton from './cards/InfoButton';
import { useState, useEffect } from 'react';
import {ReactComponent as NewWindow} from '../../assets/NewWindow.svg';
import {ReactComponent as NextArrow} from '../../assets/NextArrow.svg';
import rehypeRaw from 'rehype-raw';

export const scrollToEnd = (behavior) => {
    setTimeout(() => {
        document.getElementById('endOfMessages').scrollIntoView({ behavior: behavior });
    }, 0)
}

const isOpenCdo = ((process.env.REACT_APP_CDO_NAME || '').toLowerCase() === 'open');

export const nextButtonWidthClass = (isMobile, isTablet) => {
    return (isOpenCdo && !isMobile && !isTablet) ? '' : 'button-width-next';
};

export const useNextButtonWidthClass = () => {
    const { isMobile } = useDetectMobile();
    const { isTablet } = useDetectTablet();

    return nextButtonWidthClass(isMobile, isTablet);
};

export const getButtonWithArrow = (text, arrowSize = '16px') => {
    if (isOpenCdo) {
        return (
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{text}</span>
                <NextArrow className='next-option-arrow' style={{ display: 'inline-block' }} />
            </span>
        );
    }
    
    return text;
};

export const getMarkDown = (message) => {
    if (!message) return "";

    if (typeof message !== 'string' && !(message instanceof String))
        if (typeof message.text !== 'string' && !(message.text instanceof String))
            return ""

    const markdown = (typeof message.text === 'string' || message.text instanceof String)
        ? preprocessMarkdown(message.text, message.hyperlinks)  // assume message is a message object
        : preprocessMarkdown(message)                           // assume message markdown string

    return <ReactMarkdown rehypePlugins={[rehypeRaw]} children={markdown} components={{ a: Hyperlink }} />;
}

export const getInlineMarkDown = (message) => {
    if (!message) return "";

    if (typeof message !== 'string' && !(message instanceof String))
        if (typeof message.text !== 'string' && !(message.text instanceof String))
            return ""

    const markdown = (typeof message.text === 'string' || message.text instanceof String)
        ? preprocessMarkdown(message.text, message.hyperlinks)
        : preprocessMarkdown(message)

    return <ReactMarkdown rehypePlugins={[rehypeRaw]} children={markdown} components={{ a: Hyperlink, p: ({ children }) => <>{children}</> }} />;
}

const preprocessMarkdown = (markdown, hyperlinks) => {
    if (!markdown || !(typeof markdown === 'string' || markdown instanceof String))
        return markdown;

    let i = 0;

    markdown = markdown.replace(/<br><br>/g, '\n\n');
    markdown = markdown.replace(/<br>/g, '  \n');

    while (markdown.indexOf('##hyperlink') !== -1) {
        (Array.isArray(hyperlinks) && hyperlinks[i])
            ? markdown = markdown.replace('##hyperlink', `[${hyperlinks[i].display_text}](${hyperlinks[i].value}|${JSON.stringify(hyperlinks[i].ga_Identifier)})`)
            : markdown = markdown.replace('##hyperlink', '')
        i++;
    }

    while (markdown.indexOf('##hyperlink') !== -1) {
        (Array.isArray(hyperlinks) && hyperlinks[i])
            ? markdown = markdown.replace('##hyperlink', `[${hyperlinks[i].display_text}](${hyperlinks[i].value}|${JSON.stringify(hyperlinks[i].ga_Identifier)})`)
            : markdown = markdown.replace('##hyperlink', '')
        i++;
    }

    return markdown;
}

const Hyperlink = (props) => {
    const hyperlinkPressed = (e) => {
        if (Array.isArray(props) && props.length && props[0] instanceof Function) {
            e.preventDefault();
        }
    };

    return <span>
        {
            (props.href.startsWith('-'))
                ?
                <a
                    href=''
                    className='-anchor'
                >
                    {props.children}
                </a>
                :
                <a
                    href={props.href}
                    onClick={(e) => { hyperlinkPressed(e) }}
                    rel="noopener noreferrer"
                    target='_blank'
                    aria-describedby="NewWindow"
                >
                    {props.children}
                    <NewWindow 
                        style={{
                            "top": "4px",
                            "marginLeft": "4px",
                            "position": "relative",
                        }}
                    />
                    <span hidden id='NewWindow'>opens in new window</span>
                </a>
        }
    </span>
}

export const getMarkDownWithInfoButton = (text, explication, onShowInfo, disabled, infoMean) => {
    return <InfoButton
        disabled={disabled}
        header={infoMean}
        text={text}
        explication={explication}
        onShowInfo={onShowInfo}
    />
}

function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;
    return {
        width,
        height
    };
}

export function useDetectMobile(breakpoint = 640) {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());


    useEffect(() => {
        var userAgent = navigator.userAgent || navigator.vendor || window.opera;
        if (!/android/i.test(userAgent)) {
            function handleResize() {
                setWindowDimensions(getWindowDimensions());
            }

            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, []);

    const { width } = windowDimensions;
    return { isMobile: width <= breakpoint };
}



export function useDetectIOS() {
      const [isIOS, setIsIOS] = useState(false);
    
      useEffect(() => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const maxTouchPoints = navigator.maxTouchPoints;
        // Checks for iPhone or iPad
        const isClassicIpad = /iPad/.test(userAgent);
        const isModernIpad = userAgent.includes("Macintosh") && maxTouchPoints >= 1;
        const isModernIphone = userAgent.includes("iPhone");

        setIsIOS(isClassicIpad || isModernIpad || isModernIphone);
      }, []);

     return  {isIOS} ;
    }

export function useDetectAndroid() {
    const [isAndroid, setIsAndroid] = useState(false);

    useEffect(() => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        // Checks for Android phone or tablet
        const isAndroidPhone = /Android.*Mobile/.test(userAgent);
        const isAndroidTablet = /Android(?!.*Mobile)/.test(userAgent);
        setIsAndroid(isAndroidPhone || isAndroidTablet);
    }, []);

    return { isAndroid };
}
    
export function useDetectTablet(breakpoint = 780) {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());


    useEffect(() => {
        var userAgent = navigator.userAgent || navigator.vendor || window.opera;
        if (!/android/i.test(userAgent)) {
            function handleResize() {
                setWindowDimensions(getWindowDimensions());
            }

            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, []);

    const { width } = windowDimensions;
    return { isTablet: width <= breakpoint };
}


export const addDetectUserEvents = (callback) => {
    if (!(callback instanceof Function))
        callback = () => { };

    let bodyElement = document.getElementsByTagName('body')[0];

    bodyElement.addEventListener('touchmove', callback);
    bodyElement.addEventListener('touchmove', callback);
    bodyElement.addEventListener('mousemove', callback);
    bodyElement.addEventListener('wheel', callback);
}

export const removeDetectUserEvents = (callback) => {
    if (!(callback instanceof Function))
        callback = () => { };

    let bodyElement = document.getElementsByTagName('body')[0];

    bodyElement.removeEventListener('touchmove', callback)
    bodyElement.removeEventListener('click', callback)
    bodyElement.removeEventListener('mousemove', callback)
    bodyElement.removeEventListener('wheel', callback)
}

export const logEventHub = async (message, agg, asyncHBID, type = 'info', retry) => {
    var success = false;
    retry = (retry) ? ++retry : 1;
    let requestHeaders = {
        "Content-Type": "application/json",
         "Accept": "application/json"
      }; 

    if (asyncHBID) {
        requestHeaders['Correlation-Id'] = asyncHBID;
    }

    const env = process.env.REACT_APP_ASYNC_ENVIRONMENT;
    
    if (env && env.toLowerCase() !== "production") {
        requestHeaders['x-upstream-env'] = "nonprod";
    }
    axios({
        method: "post",
        url: process.env.REACT_APP_EVENT_HUB_URL,
        timeout: 0,
        headers: requestHeaders,
        data: {
            "agg": agg,
            "type": type,
            "message": message,
            "asyncHBID": asyncHBID,
            "env": env
        }
    }).then((response) => {
        console.log('Logging to event hubs successful.');
        success = (response.status === 200);
    }).catch((error) => {
    }).finally(() => {
        if (!success) {
            console.warn('Logging to event hubs failed.');
            if (retry < 3)
                logEventHub(message, agg, asyncHBID, type, retry)
        }
    })
}

export function formatCardLoadTiming(totalSeconds) {
       let seconds =  Math.floor(totalSeconds);
       let milliseconds = Math.round((totalSeconds - seconds) * 1000);
       if (milliseconds === 1000) { seconds += 1; milliseconds = 0 }
       return `${seconds.toString().padStart(2, '0')}:${milliseconds.toString().padStart(3, '0')}`
    // const seconds = Math.floor(totalSeconds);
    // const hundredths = Math.round((totalSeconds - seconds) * 100);
    // return `${seconds.toString().padStart(2, '0')}:${hundredths.toString().padStart(2, '0')}`;
}

